const SESSION_STORAGE_KEY = 'analytics-session'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

type StoredSession = {
  id: string
  startTime: number
  lastActivity: number
  startSent?: boolean
}

let cachedSession: StoredSession | null = null
let startSentForSessionId: string | null = null
let endSentForSessionId: string | null = null

export type AnalyticsSession = StoredSession

const isBrowser = () => typeof window !== 'undefined'

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

const readSession = (): StoredSession | null => {
  if (!isBrowser()) return null
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as StoredSession
    if (
      !parsed ||
      typeof parsed.id !== 'string' ||
      typeof parsed.startTime !== 'number' ||
      typeof parsed.lastActivity !== 'number'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const writeSession = (session: StoredSession) => {
  if (!isBrowser()) return
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

const createSession = (now: number): StoredSession => ({
  id: generateSessionId(),
  startTime: now,
  lastActivity: now,
  startSent: false,
})

const isExpired = (session: StoredSession, now: number) =>
  now - session.lastActivity > SESSION_TIMEOUT_MS

export const getSessionStateForEvent = (): {
  session: StoredSession | null
  endedSession: StoredSession | null
} => {
  if (!isBrowser()) return { session: null, endedSession: null }

  const now = Date.now()
  let session = cachedSession ?? readSession()
  let endedSession: StoredSession | null = null

  if (!session || isExpired(session, now)) {
    if (session && endSentForSessionId !== session.id) {
      endedSession = session
      endSentForSessionId = session.id
    }
    session = createSession(now)
  } else {
    session = { ...session, lastActivity: now }
  }

  cachedSession = session
  writeSession(session)
  return { session, endedSession }
}

export const getActiveSession = (): StoredSession | null => {
  if (!isBrowser()) return null

  const now = Date.now()
  const session = cachedSession ?? readSession()
  if (!session || isExpired(session, now)) {
    return null
  }
  return session
}

export const getSessionForEvent = (): StoredSession | null => {
  return getSessionStateForEvent()?.session ?? null
}

export const reserveSessionStartSend = (session: StoredSession): boolean => {
  if (session.startSent || startSentForSessionId === session.id) {
    return false
  }

  startSentForSessionId = session.id
  const updated: StoredSession = { ...session, startSent: true }
  cachedSession = updated
  writeSession(updated)
  return true
}
